import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { ClassEntityService } from '../../../../services/entity-services/classes-entity.service';
import { PolicyTemplateService, ResourcePolicyLink } from '../../../../services/entity-services/policy-template.service';
import { PolicyTemplateSummary } from '../../../../interfaces/policy-templates/policy-template-summary.interface';
import { Log, IShortLogger } from '../../../../services/logger.service';

/** Normalised representation of one resource-template entry. */
interface ResourceEntry {
  id: string;
  name: string;
  /** Canonical capitalised form: 'Vm' | 'UserAccount' */
  resourceType: 'Vm' | 'UserAccount';
  /** Previously saved policy template ID — may be empty string */
  policyTemplateId: string;
}

/** Minimal class shape used by this component. */
interface ClassSummary {
  id: string;
  name: string;
  stage?: string;
  status?: string;
  trainingSchedule?: {
    title?: string;
    course?: { code?: string; name?: string } | null;
    dateRanges?: any[];
  } | null;
  resourceEntries: ResourceEntry[];
}

const VM_TYPES    = new Set(['vm', 'Vm']);
const UA_TYPES    = new Set(['useraccount', 'UserAccount', 'acc', 'subscription']);

function canonicalType(raw: string): 'Vm' | 'UserAccount' | null {
  if (VM_TYPES.has(raw)) return 'Vm';
  if (UA_TYPES.has(raw.toLowerCase())) return 'UserAccount';
  return null;
}

@Component({
  selector: 'app-policy-template-linking',
  templateUrl: './policy-template-linking.component.html',
  styleUrl: './policy-template-linking.component.css',
})
export class PolicyTemplateLinkingComponent implements OnInit, AfterViewInit {
  private readonly logger: IShortLogger<PolicyTemplateLinkingComponent> =
    Log.create(PolicyTemplateLinkingComponent);

  @ViewChild('stepper') stepper!: MatStepper;

  // ── Stepper forms ─────────────────────────────────────────────────────
  /** Steps 1–3 are read-only placeholders so the stepper shows them as complete. */
  readonlyForm = new FormGroup({});
  policyForm   = new FormGroup({
    vmPolicyId: new FormControl<string>(''),
    uaPolicyId: new FormControl<string>(''),
  });
  reviewForm  = new FormGroup({});

  // ── Loading / saving state ─────────────────────────────────────────────
  isPageLoading = true;
  isSaving      = false;

  // ── Class data ────────────────────────────────────────────────────────
  classId: string | null     = null;
  classSummary: ClassSummary | null = null;

  // ── Resource type flags ───────────────────────────────────────────────
  hasVm = false;
  hasUa = false;

  // ── Policy template options ───────────────────────────────────────────
  vmPolicies: PolicyTemplateSummary[] = [];
  uaPolicies: PolicyTemplateSummary[] = [];

  // ── Policy template detail drawer ────────────────────────────────────
  /** Set to a PolicyTemplateSummary to open the drawer; set to null to close it. */
  drawerPolicy: PolicyTemplateSummary | null = null;

  constructor(
    private readonly route:               ActivatedRoute,
    readonly         router:              Router,
    private readonly classEntityService:  ClassEntityService,
    private readonly policyService:       PolicyTemplateService,
    private readonly snackBar:            MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.classId = params['classId'] ?? null;
      if (this.classId) {
        this.loadClass(this.classId);
      } else {
        this.isPageLoading = false;
      }
    });
  }

  ngAfterViewInit(): void {
    // Intentionally empty — navigation to Policy Selection step happens after
    // data loads (see loadPolicies), since the stepper is hidden by *ngIf until then.
  }

  // ── Data loading ────────────────────────────────────────────────────────

  private loadClass(classId: string): void {
    this.classEntityService.getClassByClassId(classId).subscribe({
      next: (raw: any) => {
        this.classSummary = this.normaliseClass(raw);
        this.resolveResourceTypes();
        this.loadPolicies();
      },
      error: err => {
        this.logger.e('Failed to load class', err);
        this.isPageLoading = false;
        this.snackBar.open('Failed to load class details.', 'Dismiss', { duration: 5000 });
      },
    });
  }

  private normaliseClass(raw: any): ClassSummary {
    const schedule = raw?.trainingSchedule ?? raw?.TrainingSchedule;
    const rawEntries: any[] =
      schedule?.resourceTemplateIds ?? schedule?.ResourceTemplateIds ?? [];

    const resourceEntries: ResourceEntry[] = rawEntries
      .map((e: any): ResourceEntry | null => {
        const rt  = e?.resourceType ?? e?.ResourceType ?? '';
        const canon = canonicalType(rt);
        return canon
          ? {
              id:               e?.id   ?? e?.Id   ?? '',
              name:             e?.name ?? e?.Name ?? '',
              resourceType:     canon,
              policyTemplateId: e?.policyTemplateId ?? e?.PolicyTemplateId ?? '',
            }
          : null;
      })
      .filter((e): e is ResourceEntry => e !== null);

    return {
      id:     raw?.id   ?? raw?._id ?? '',
      name:   raw?.name ?? raw?.Name ?? '',
      stage:  raw?.stage,
      status: raw?.status,
      trainingSchedule: {
        title:  schedule?.title  ?? schedule?.Title,
        course: schedule?.course ?? schedule?.Course,
        dateRanges: schedule?.dateRanges ?? schedule?.DateRanges ?? [],
      },
      resourceEntries,
    };
  }

  private resolveResourceTypes(): void {
    const entries = this.classSummary?.resourceEntries ?? [];
    this.hasVm = entries.some(e => e.resourceType === 'Vm');
    this.hasUa = entries.some(e => e.resourceType === 'UserAccount');
  }

  private loadPolicies(): void {
    const vm$ = this.hasVm
      ? this.policyService.getByType('Vm').pipe(catchError(() => of([])))
      : of([]);
    const ua$ = this.hasUa
      ? this.policyService.getByType('UserAccount').pipe(catchError(() => of([])))
      : of([]);

    forkJoin({ vm: vm$, ua: ua$ })
      .pipe(finalize(() => {
        this.isPageLoading = false;
        // Navigate to Policy Selection (index 3) once the *ngIf renders the stepper.
        setTimeout(() => {
          if (this.stepper) this.stepper.selectedIndex = 3;
        }, 0);
      }))
      .subscribe(({ vm, ua }) => {
        this.vmPolicies = vm as PolicyTemplateSummary[];
        this.uaPolicies = ua as PolicyTemplateSummary[];
        this.preSelectExisting();
      });
  }

  private preSelectExisting(): void {
    const entries = this.classSummary?.resourceEntries ?? [];

    if (this.hasVm) {
      const vmEntry = entries.find(e => e.resourceType === 'Vm');
      const savedId = vmEntry?.policyTemplateId ?? '';
      if (savedId && this.vmPolicies.some(p => p.id === savedId)) {
        this.policyForm.get('vmPolicyId')!.setValue(savedId);
      }
    }

    if (this.hasUa) {
      const uaEntry = entries.find(e => e.resourceType === 'UserAccount');
      const savedId = uaEntry?.policyTemplateId ?? '';
      if (savedId && this.uaPolicies.some(p => p.id === savedId)) {
        this.policyForm.get('uaPolicyId')!.setValue(savedId);
      }
    }
  }

  // ── Validation ──────────────────────────────────────────────────────────

  get isPolicyStepValid(): boolean {
    const { vmPolicyId, uaPolicyId } = this.policyForm.value;
    return (
      (!this.hasVm || !!vmPolicyId) &&
      (!this.hasUa || !!uaPolicyId)
    );
  }

  // ── Helpers for template ────────────────────────────────────────────────

  get selectedVmPolicy(): PolicyTemplateSummary | null {
    const id = this.policyForm.value.vmPolicyId;
    return id ? (this.vmPolicies.find(p => p.id === id) ?? null) : null;
  }

  get selectedUaPolicy(): PolicyTemplateSummary | null {
    const id = this.policyForm.value.uaPolicyId;
    return id ? (this.uaPolicies.find(p => p.id === id) ?? null) : null;
  }

  get vmResourceEntries(): ResourceEntry[] {
    return this.classSummary?.resourceEntries.filter(e => e.resourceType === 'Vm') ?? [];
  }

  get uaResourceEntries(): ResourceEntry[] {
    return this.classSummary?.resourceEntries.filter(e => e.resourceType === 'UserAccount') ?? [];
  }

  get courseName(): string {
    const c = this.classSummary?.trainingSchedule?.course;
    return c?.name ?? '—';
  }

  get courseCode(): string {
    const c = this.classSummary?.trainingSchedule?.course;
    return c?.code ?? '—';
  }

  // ── Drawer ─────────────────────────────────────────────────────────────

  openDrawer(policy: PolicyTemplateSummary): void {
    this.drawerPolicy = policy;
  }

  closeDrawer(): void {
    this.drawerPolicy = null;
  }

  // ── Save ───────────────────────────────────────────────────────────────

  onSave(): void {
    if (!this.classId || !this.isPolicyStepValid) return;

    const links: ResourcePolicyLink[] = [];

    if (this.hasVm && this.policyForm.value.vmPolicyId) {
      links.push({ resourceType: 'Vm', policyTemplateId: this.policyForm.value.vmPolicyId! });
    }
    if (this.hasUa && this.policyForm.value.uaPolicyId) {
      links.push({ resourceType: 'UserAccount', policyTemplateId: this.policyForm.value.uaPolicyId! });
    }

    this.isSaving = true;
    this.policyService.linkToClass(this.classId, links).subscribe({
      next: () => {
        this.isSaving = false;
        this.snackBar.open('Policy templates linked successfully.', 'OK', { duration: 4000 });
        this.router.navigate(['/dashboard']);
      },
      error: err => {
        this.logger.e('Failed to save policy linkage', err);
        this.isSaving = false;
        this.snackBar.open('Failed to save policy linkage. Please try again.', 'Dismiss', { duration: 6000 });
      },
    });
  }
}
